import { useQuery, useMutation } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Product } from "@shared/operations-schema";
import { useCurrentBusiness } from "./use-current-business";

export type { Product } from "@shared/operations-schema";

export type ProductInput = Omit<Partial<Product>, "price" | "cost" | "taxRate"> & {
  price?: string | number;
  cost?: string | number | null;
  taxRate?: string | number | null;
};

export function useProducts() {
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  const businessId = currentBusiness?.id;

  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery<Product[], Error>({
    queryKey: ["/api/sales/products", businessId],
    queryFn: async () => {
      if (!businessId) return [];
      const res = await fetch(`/api/sales/products`, { credentials: "include" });
      if (!res.ok) {
        if (res.status === 400 || res.status === 401) return [];
        throw new Error("Failed to fetch products");
      }
      return res.json() as Promise<Product[]>;
    },
    enabled: !!businessId,
  });

  const createProductMutation = useMutation({
    mutationFn: async (data: ProductInput) => {
      return apiRequest("POST", "/api/sales/products", {
        ...data,
      });
    },
    onSuccess: () => {
      toast({
        title: "Product created",
        description: "The product has been successfully created.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/products", businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error creating product",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateProductMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: ProductInput }) => {
      return apiRequest("PATCH", `/api/sales/products/${id}`, {
        ...data,
      });
    },
    onSuccess: () => {
      toast({
        title: "Product updated",
        description: "The product has been successfully updated.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/products", businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error updating product",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteProductMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/sales/products/${id}`);
    },
    onSuccess: () => {
      toast({
        title: "Product deleted",
        description: "The product has been successfully deleted.",
      });
      queryClient.invalidateQueries({ queryKey: ["/api/sales/products", businessId] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error deleting product",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    products,
    isLoading,
    error,
    createProductMutation,
    updateProductMutation,
    deleteProductMutation,
  };
}
